/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Icon, IIconStyleProps, IIconStyles } from '@fluentui/react/lib/Icon';
import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { ExpandableCard } from 'src/core/components/ExpandableCard';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylist } from 'src/core/themes/luisStylist';

export type QnaMakerProjectDeployedCardProps = {
    isDeploymentFailed: boolean;
};

const messages = defineMessages({
    tierLabel: { id: 'qna.qnaMakerProjectDeployedCard.tierLabel', defaultMessage: 'Tier:' },
    deployed: { id: 'qna.qnaMakerProjectDeployedCard.deployed', defaultMessage: 'Deployed' },
    stateLabel: { id: 'qna.qnaMakerProjectDeployedCard.stateLabel', defaultMessage: 'State:' },
    stateCalloutDescription: {
        id: 'qna.qnaMakerProjectDeployedCard.stateCalloutDescription',
        defaultMessage: 'Your knowledge base has been deployed to production and is now available for use in your Bot or App.'
    },
    resourceLabel: { id: 'qna.qnaMakerProjectDeployedCard.resourceLabel', defaultMessage: 'Resource:' },
    locationLabel: { id: 'qna.qnaMakerProjectDeployedCard.locationLabel', defaultMessage: 'Location:' },
    cardSubtitle: { id: 'qna.qnaMakerProjectDeployedCard.cardSubtitle', defaultMessage: 'Knowledge base status' },
    deploymentDateLabel: { id: 'qna.qnaMakerProjectDeployedCard.deploymentDateLabel', defaultMessage: 'Deployment Date:' },
    deploymentDateCalloutDescription: {
        id: 'qna.qnaMakerProjectDeployedCard.deploymentDateCalloutDescription',
        defaultMessage: 'The most recent date of deployment.'
    },
    deploymentTimeLabel: { id: 'qna.qnaMakerProjectDeployedCard.deploymentTimeLabel', defaultMessage: 'Deployment Time:' },
    deploymentTimeCalloutDescription: {
        id: 'qna.qnaMakerProjectDeployedCard.deploymentTimeCalloutDescription',
        defaultMessage: 'The most recent time of deployment.'
    },
    cardTitleIfDeploymentCompleted: {
        id: 'qna.qnaMakerProjectDeployedCard.cardTitleIfDeploymentCompleted',
        defaultMessage: 'Your knowledge base is now deployed. You can get your prediction URL or create a bot.'
    },
    cardTitleIfDeploymentFailed: {
        id: 'qna.qnaMakerProjectDeployedCard.cardTitleIfDeploymentFailed',
        defaultMessage:
            'Your latest deployment has failed. Your knowledge base is using the last successful deployment\
            from {date} and your can still get your prediction URL or create a bot.'
    },
    cardName: {
        id: 'qna.qnaMakerProjectDeployedCard.cardName',
        defaultMessage: 'Qna maker project deployed card.'
    }
});

const cardTheme = getScopedTheme('QnaMakerProjectDeployedCard');
const { getClassName } = getStylist('QnaMakerProjectDeployedCard');
const infoIconClassName = getClassName('infoIconClassName', { fontSize: '12px' });
const completedIconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({
    root: { fontSize: '16px', color: cardTheme.completedIconColor }
});

export const QnaMakerProjectDeployedCard = observer((props: QnaMakerProjectDeployedCardProps) => {
    const { isDeploymentFailed } = props;
    const { rootStore } = useGlobalState();
    const { locale, localize } = useLocalization();

    const currentProject = rootStore.currentQnaMakerProjectStore;
    const { csAccountStore: currentResource } = rootStore.azureStore.session;

    const cardHeader = (
        <MessageBar
            messageBarType={isDeploymentFailed ? MessageBarType.error : MessageBarType.success}
            styles={{ innerText: { width: '100%' } }}>
            <Stack horizontal grow horizontalAlign="space-between">
                <LuisText>
                    {isDeploymentFailed
                        ? localize(messages.cardTitleIfDeploymentFailed, {
                              date: currentProject.lastPublishedDate?.toLocaleDateString(locale) ?? '--'
                          })
                        : localize(messages.cardTitleIfDeploymentCompleted)}
                </LuisText>
                <Icon iconName="DoubleChevronUp" />
            </Stack>
        </MessageBar>
    );

    return (
        <ExpandableCard
            isExpanded
            isChevronHidden
            onRenderHeader={cardHeader}
            isSeparatorDisplayed={false}
            cardName={localize(messages.cardName)}
            styles={{ headerContainer: { padding: 0 }, root: { flex: 0 } }}>
            <Stack tokens={{ childrenGap: 12, padding: '13px 36px' }}>
                <LuisText fontWeight={600}>{localize(messages.cardSubtitle)}</LuisText>

                <Stack horizontal tokens={{ childrenGap: 40 }}>
                    <Stack tokens={{ childrenGap: 13 }}>
                        <Stack horizontal verticalAlign="center">
                            <LuisText fontWeight={600} variant="smallPlus" lineHeight="16px" color={cardTheme.titleTextColor}>
                                {localize(messages.stateLabel)}
                            </LuisText>
                            <CalloutButton content={localize(messages.stateCalloutDescription)} iconClassName={infoIconClassName} />
                        </Stack>
                        <Stack horizontal verticalAlign="center">
                            <LuisText fontWeight={600} variant="smallPlus" lineHeight="16px" color={cardTheme.titleTextColor}>
                                {localize(messages.deploymentDateLabel)}
                            </LuisText>
                            <CalloutButton
                                content={localize(messages.deploymentDateCalloutDescription)}
                                iconClassName={infoIconClassName}
                            />
                        </Stack>
                        <Stack horizontal verticalAlign="center">
                            <LuisText fontWeight={600} variant="smallPlus" lineHeight="16px" color={cardTheme.titleTextColor}>
                                {localize(messages.deploymentTimeLabel)}
                            </LuisText>
                            <CalloutButton
                                content={localize(messages.deploymentTimeCalloutDescription)}
                                iconClassName={infoIconClassName}
                            />
                        </Stack>
                    </Stack>

                    <Stack tokens={{ childrenGap: 13 }}>
                        <Stack horizontal verticalAlign="center" horizontalAlign="space-between" tokens={{ childrenGap: 30 }}>
                            <LuisText fontWeight={600} variant="smallPlus" lineHeight="16px">
                                {localize(messages.deployed)}
                            </LuisText>
                            <Icon iconName="Completed" styles={completedIconStyles} />
                        </Stack>
                        <Stack horizontal verticalAlign="center" horizontalAlign="space-between" tokens={{ childrenGap: 30 }}>
                            <LuisText fontWeight={600} variant="smallPlus" lineHeight="16px">
                                {currentProject.lastPublishedDate?.toLocaleDateString(locale) ?? '--'}
                            </LuisText>
                            <Icon iconName="Completed" styles={completedIconStyles} />
                        </Stack>
                        <Stack horizontal verticalAlign="center" horizontalAlign="space-between" tokens={{ childrenGap: 30 }}>
                            <LuisText fontWeight={600} variant="smallPlus" lineHeight="16px">
                                {currentProject.lastPublishedDate?.toLocaleTimeString(locale) ?? '--'}
                            </LuisText>
                            <Icon iconName="Completed" styles={completedIconStyles} />
                        </Stack>
                    </Stack>

                    <Stack tokens={{ childrenGap: 13 }}>
                        <LuisText fontWeight={600} variant="smallPlus" color={cardTheme.titleTextColor}>
                            {localize(messages.resourceLabel)}
                        </LuisText>
                        <LuisText fontWeight={600} variant="smallPlus" color={cardTheme.titleTextColor}>
                            {localize(messages.locationLabel)}
                        </LuisText>
                        <LuisText fontWeight={600} variant="smallPlus" lineHeight="16px" color={cardTheme.titleTextColor}>
                            {localize(messages.tierLabel)}
                        </LuisText>
                    </Stack>

                    <Stack tokens={{ childrenGap: 13 }}>
                        <LuisText fontWeight={600} variant="smallPlus" lineHeight="16px">
                            {currentResource?.name}
                        </LuisText>
                        <LuisText fontWeight={600} variant="smallPlus" lineHeight="16px">
                            {currentResource?.region}
                        </LuisText>
                        <LuisText fontWeight={600} variant="smallPlus" lineHeight="16px">
                            {currentResource?.sku.name} ({currentResource?.sku.code})
                        </LuisText>
                    </Stack>
                </Stack>
            </Stack>
        </ExpandableCard>
    );
});
